import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { useRetrieveCurrentPlanzone } from "@planzone/view-models";
import { FolderIcon } from "@common/folderIcon";
import { ArrowIcon } from "@common/assets/arrowIcon";
import { TagIcon } from "@common/assets/tagIcon";
import { GroupIcon } from "@common/assets/groupIcon";
import { UserIcon } from "@common/assets/userIcon";
import { ObsIcon } from "@common/assets/obsIcon";
import { ProjectPortfolioIcon } from "@common/projectPortfolioIcon";
import { ResourceAvatar } from "@common/resourceAvatar";
import { Views } from "../types";
import { NameWithCount } from "./nameWithCount";
import { Props } from "./type";
import styles from "./styles.scss";

function Input({ filter, placeholder, views, isOpen }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const isActivated = (view: Views) => !!views.find((v) => v === view);
  const projects =
    filter.projects && (isActivated("projects") || isActivated("zones"))
      ? filter.projects
      : [];
  const projectsElementsTags =
    filter.projectsElementsTags && isActivated("projectsElementsTags")
      ? filter.projectsElementsTags
      : [];
  const resourceTags =
    filter.resourceTags && isActivated("resourceTags")
      ? filter.resourceTags
      : [];
  const projectsTags =
    filter.projectsTags && isActivated("projectsTags")
      ? filter.projectsTags
      : [];
  const resources =
    filter.resources && isActivated("resources") ? filter.resources : [];
  const groups = filter.groups && isActivated("groups") ? filter.groups : [];
  const projectStatuses =
    planzone.uuid === "57ec7e80-dcbb-49af-b11f-665fa6e545c1"
      ? []
      : filter.projectStatuses && isActivated("projectStatuses")
        ? filter.projectStatuses
        : [];
  const projectPortfolios =
    filter.projectPortfolios && isActivated("projectPortfolios")
      ? filter.projectPortfolios
      : [];
  const roles = filter.roles && isActivated("roles") ? filter.roles : [];
  // Unchecked units only narrow the picked ones, they are not part of the summary.
  const organizationalUnits =
    filter.organizationalUnits && isActivated("organizationalUnits")
      ? filter.organizationalUnits.filter(({ isExcluded }) => !isExcluded)
      : [];

  return (
    <div
      className={classNames(styles.input, {
        [styles.opened]: isOpen,
      })}
    >
      {!projects.length &&
        !projectsElementsTags.length &&
        !resourceTags.length &&
        !resources.length &&
        !groups.length &&
        !projectStatuses.length &&
        !projectsTags.length &&
        !projectPortfolios.length &&
        !roles.length &&
        !organizationalUnits.length && (
          <p className={styles.placeholder}>{placeholder}</p>
        )}
      {!!projects.length && (
        <NameWithCount
          name={projects[0].name}
          nbr={projects.length - 1}
          icon={<FolderIcon className={styles.folder} />}
        />
      )}
      {!!resources.length && (
        <NameWithCount
          name={resources[0].name}
          nbr={resources.length - 1}
          icon={
            <div className={styles.icon}>
              <ResourceAvatar resource={resources[0]} />
            </div>
          }
        />
      )}
      {!!resourceTags.length && (
        <NameWithCount
          name={resourceTags[0].name}
          nbr={resourceTags.length - 1}
          icon={<TagIcon className={styles.tagIcon} />}
        />
      )}
      {!!groups.length && (
        <NameWithCount
          name={groups[0].name}
          nbr={groups.length - 1}
          icon={<GroupIcon className={styles.icon} />}
        />
      )}
      {!!projectsElementsTags.length && (
        <NameWithCount
          name={projectsElementsTags[0].name}
          nbr={projectsElementsTags.length - 1}
          icon={<TagIcon className={styles.tagIcon} />}
        />
      )}
      {!!projectsTags.length && (
        <NameWithCount
          name={projectsTags[0].name}
          nbr={projectsTags.length - 1}
          icon={<TagIcon className={styles.tagIcon} />}
        />
      )}
      {!!projectPortfolios.length && (
        <NameWithCount
          name={projectPortfolios[0].name}
          nbr={projectPortfolios.length - 1}
          icon={<ProjectPortfolioIcon className={styles.icon} />}
        />
      )}
      {!!projectStatuses.length && (
        <NameWithCount
          name={t(
            `projectStatuses.${projectStatuses[0].label}`,
            projectStatuses[0].label,
          )}
          nbr={projectStatuses.length - 1}
          icon={<FolderIcon className={styles.icon} />}
        />
      )}
      {!!roles.length && (
        <NameWithCount
          name={t(`roles.${roles[0].name}`, roles[0].name)}
          nbr={roles.length - 1}
          icon={<UserIcon className={styles.icon} />}
        />
      )}
      {!!organizationalUnits.length && (
        <NameWithCount
          name={organizationalUnits[0].name}
          nbr={organizationalUnits.length - 1}
          icon={<ObsIcon className={styles.icon} />}
        />
      )}
      <ArrowIcon className={styles.toggleIcon} />
    </div>
  );
}

export { Input };
